import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User, AuthChangeEvent, Session, FunctionsHttpError, isAuthRefreshDiscardedError, isAuthRetryableFetchError } from '@supabase/supabase-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { GetUser, UserModel } from '../../models/user.model';
import { Roles } from '../../constants/menu-item';
import { user_table } from '../../constants/table-names';
import { SupabaseService } from '../supabase-service/supabase.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUser: BehaviorSubject<User | boolean | null> = new BehaviorSubject<User | boolean | null>(null)
  private currentUserProfile: BehaviorSubject<GetUser | boolean | null> = new BehaviorSubject<GetUser | boolean | null>(null)

  user_table = user_table

  constructor(private router: Router, private supabaseService: SupabaseService) {


    this.supabaseService.supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {

        if (sess != null && "user" in sess) {
          this.currentUser.next(sess.user)
        }
      } else if (event === 'SIGNED_OUT') {
        // Fired on explicit sign-out and when supabase-js fails to refresh an
        // expired session, so the user is sent to sign-in instead of being left
        // on a page whose queries now fail.
        this.clearLocalAuthState()
        // Before the first navigation completes, the route guards handle this.
        if (this.router.navigated && !this.router.url.startsWith('/sign-in')) {
          this.router.navigate(['/sign-in'])
        }
      }
    })

    // Trigger initial session load
    this.loadUser()
  }

  async loadUser() {
    if (this.currentUser.value) {
      // User is already set, no need to do anything else
      return
    }
    const user = await this.supabaseService.supabase.auth.getUser()

    if (user.data.user) {
      this.currentUser.next(user.data.user)
      this.getProfile()
    } else {
      this.currentUser.next(false)
    }
  }

  async getProfile(): Promise<Observable<boolean | GetUser | null>> {
    const profile_data = localStorage.getItem('user')

    if (profile_data != null) {
      const profile = JSON.parse(profile_data)
      this.currentUserProfile.next(profile);
      return this.currentUserProfile.asObservable()
    } else {

      if (this.currentUserProfile.value) {
        return this.currentUserProfile.asObservable()
      }

      if (this.getCurrentUserId() == null) {
        return this.currentUserProfile.asObservable()
      }

      await this.supabaseService.supabase.from(this.user_table.table_name).select('*').eq(this.user_table.uuid, this.getCurrentUserId()).then(async (response) => {
        if (response.error) {
          this.currentUserProfile.next(false)
        } else {
          if (undefined !== response.data[0]) {
            const profile = new GetUser({
              user_id: response.data[0].user_id,
              username: response.data[0].username,
              email: response.data[0].email,
              user_role: response.data[0].user_role,
            })
            localStorage.setItem('user_role', profile.user_role?.toString() || '')
            localStorage.setItem('user', JSON.stringify(profile));
            this.currentUserProfile.next(profile)
          }
        }
      });
      return this.currentUserProfile.asObservable()
    }


  }


  // check if local storage have profile_role then its ok else call getProfile
  async getCurrentProfileOnLocalStorage(): Promise<Observable<boolean | GetUser | null>> {

    const user = localStorage.getItem('user_role');
    if (user != null) {
      return this.currentUserProfile.asObservable();
    } else {
      await this.getProfile()

      return this.currentUserProfile.asObservable();
    }
  }

  signUp(credentials: { email: string; password: string, options: any }) {
    //@ts-ignore
    // credentials = {...credentials, options: {data: metadata}}
    return this.supabaseService.supabase.auth.signUp(credentials)
  }

  /**
   * Admin only. Creates a confirmed login (no verification email) plus its
   * tbl_user row via the `admin-users` Edge Function, which holds the
   * service-role key and re-checks that the caller is an ADMIN.
   */
  createSystemUser(user: { username: string; email: string; user_role: string; password: string }): Promise<UserModel> {
    return this.invokeAdminUsers({ action: 'create_user', ...user })
  }

  /** Admin only. Sets the password of any user via the `admin-users` Edge Function. */
  async setUserPassword(user_id: number, password: string): Promise<void> {
    await this.invokeAdminUsers({ action: 'set_password', user_id, password })
  }

  private async invokeAdminUsers(body: Record<string, unknown>): Promise<any> {
    const { data, error } = await this.supabaseService.supabase.functions.invoke('admin-users', { body })

    if (error) {
      // The function returns { error: message } on 4xx/5xx; show that instead
      // of the generic "non-2xx status code" message.
      if (error instanceof FunctionsHttpError) {
        const payload = await error.context.json().catch(() => null)
        throw new Error(payload?.error || error.message)
      }
      throw error
    }

    return data
  }

  signIn(credentials: { email: string; password: string }) {
    return this.supabaseService.supabase.auth.signInWithPassword(credentials)
  }

  updateUser(password: string) {
    return this.supabaseService.supabase.auth.updateUser({
      password: password
    })
  }

  sendPwReset(email: string) {
    return this.supabaseService.supabase.auth.resetPasswordForEmail(email, { redirectTo: 'http://localhost:4200/reset-password' })
  }

  async updateUserPassword(password: string) {
    const { error } = await this.supabaseService.supabase.auth.updateUser({
      password: password
    });

    if (error) {
      throw error;
    }

    // End the session so the user signs in again with the new password.
    await this.signOut()
  }



  async signOut() {
    await this.supabaseService.supabase.auth.signOut()
    this.clearLocalAuthState()
    this.router.navigate(['/sign-in'])
  }

  /**
   * True when Supabase holds a usable session and the matching tbl_user
   * profile is cached. getSession() renews an expired access token with the
   * refresh token, so this only fails once renewal is impossible (refresh
   * token rejected, signed out elsewhere); a stale localStorage profile is
   * then cleared.
   */
  async hasActiveSession(): Promise<boolean> {
    const auth = this.supabaseService.supabase.auth
    let { data, error } = await auth.getSession()

    // Another tab renewed the session mid-refresh; the new one is already stored.
    if (error && isAuthRefreshDiscardedError(error)) {
      ({ data, error } = await auth.getSession())
    }

    const session = data?.session

    if (!session) {
      // Renewal failed for a transient reason (offline, laptop waking up, 5xx).
      // supabase-js keeps the session and its auto-refresh keeps retrying, so
      // keep the user in the app instead of signing them out.
      if (error && isAuthRetryableFetchError(error)) {
        return this.isUserLoggedIn()
      }

      this.clearLocalAuthState()
      return false
    }

    if (this.getCurrentUserId() !== session.user.id) {
      this.currentUser.next(session.user)
    }

    if (!this.isUserLoggedIn()) {
      // getProfile() short-circuits on an in-memory profile without re-caching it.
      this.currentUserProfile.next(null)
      await this.getProfile()
    }

    return this.isUserLoggedIn()
  }

  private clearLocalAuthState() {
    localStorage.removeItem('user_role')
    localStorage.removeItem('user')
    this.currentUserProfile.next(false)
    this.currentUser.next(false)
  }

  getCurrentUser(): Observable<User | boolean | null> {
    this.getProfile()
    return this.currentUser.asObservable()
  }

  getCurrentProfile(): Observable<GetUser | boolean | null> {
    return this.currentUserProfile.asObservable()
  }

  getCurrentUserId(): string | null {
    return this.currentUser.value ? (this.currentUser.value as User).id : null;
  }

  // check if user is presnt in local storage
  isUserLoggedIn(): boolean {
    const user = localStorage.getItem('user')
    if (user != null) {
      return true
    }
    return false
  }

  get user() {
    // @ts-ignore
    return this.supabaseService.supabase.auth.getUser().then(({ data }) => data?.user)
  }

  authChanges(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    // @ts-ignore
    return this.supabaseService.supabase.auth.onAuthStateChange(callback)
  }

  // user role 
  getUserRole() {
    if (localStorage.getItem('user_role') == null) {
      return 'UNKNOWN'
    }
    return localStorage.getItem('user_role')
  }

  getUserFromLocalStorage() {
    const user = localStorage.getItem('user')
    if (user != null) {
      return JSON.parse(user)
    }
    return null
  }

  getUserIdFromLocalStorage(): number | null {
    const user = localStorage.getItem('user')
    if (user != null) {
      const userObj = JSON.parse(user)
      return userObj.user_id
    }
    return null
  }


  async getUsers(role: Roles = Roles.ANNOTATOR): Promise<GetUser[]> {
    const { data, error } = await this.supabaseService.supabase.from(this.user_table.table_name).select(`${this.user_table.user_id}, ${this.user_table.username}, ${this.user_table.email}, ${this.user_table.user_role}`)
      .neq(this.user_table.user_role, Roles.ADMIN)
      .eq(this.user_table.user_role, role);

    if (error) throw error;

    return data as unknown as GetUser[] || [];
  }

  async getPaginatedUsers(
    pageIndex: number,
    pageSize: number,
    searchText?: string,
    role?: string[]
  ): Promise<{ data: UserModel[]; totalCount: number }> {


    const from = pageIndex * pageSize;
    const to = from + pageSize - 1;

    let query = this.supabaseService.supabase
      .from(this.user_table.table_name)
      .select(`${this.user_table.user_id}, ${this.user_table.username}, ${this.user_table.email}, ${this.user_table.user_role}`, { count: 'exact' })
      .neq(this.user_table.user_id, 1);
    // .select(`*`, { count: 'exact' });

    if (searchText && searchText.trim().length > 0) {
      // Combining conditions with `username` and `email` using OR
      query = query.or(
        `username.ilike.%${searchText}%,email.ilike.%${searchText}%`
      );

    }

    if (role && role.length > 0) {
      query = query.in(this.user_table.user_role, role)
    }


    const { data, count, error } = await query.range(from, to);

    if (error) throw error;

    return { data: data as unknown as UserModel[] || [], totalCount: count || 0 };


  }

  async updateSystemUser(user_id: number, userData: Partial<Pick<UserModel, 'username' | 'email' | 'user_role'>>): Promise<UserModel> {
    if (!user_id) {
      throw new Error('Invalid user ID.');
    }

    const updateData: Partial<Pick<UserModel, 'username' | 'email' | 'user_role'>> & { updated_at: Date } = {
      updated_at: new Date(),
    };

    if (userData.username !== undefined) {
      updateData.username = userData.username.trim();
    }

    if (userData.email !== undefined) {
      updateData.email = userData.email.trim();
    }

    if (userData.user_role !== undefined) {
      updateData.user_role = userData.user_role;
    }

    const { data, error } = await this.supabaseService.supabase
      .from(this.user_table.table_name)
      .update(updateData)
      .eq(this.user_table.user_id, user_id)
      .select(`${this.user_table.user_id}, ${this.user_table.username}, ${this.user_table.email}, ${this.user_table.user_role}`)
      .single();

    if (error) throw error;

    const updatedUser = data as unknown as UserModel;
    const currentUser = this.getUserFromLocalStorage();
    if (currentUser?.user_id === user_id) {
      const updatedProfile = new GetUser({
        user_id: updatedUser.user_id,
        username: updatedUser.username,
        email: updatedUser.email,
        user_role: updatedUser.user_role,
      });
      localStorage.setItem('user_role', updatedProfile.user_role?.toString() || '');
      localStorage.setItem('user', JSON.stringify(updatedProfile));
      this.currentUserProfile.next(updatedProfile);
    }

    return updatedUser;
  }


}
